import { useEffect } from 'react';
import { useEmbedConfig } from 'features/EmbedConfig';
import useCanonicalBase from 'hooks/useCanonicalBase';
import { RouterTypes } from 'router/types';
import { isExperienceListHome } from 'router/utils';
import { encodeRoute } from 'router/queryRouteCodec';
import mergeSearchIntoUrl from 'utils/seo/mergeSearchIntoUrl';

type CanonicalLinkParams = {
  collectionId?: string | null;
  eventId?: string;
  groupId?: string | null;
};

/**
 * Build the canonical URL for an internal route in the shape the active router
 * serves it: query mode emits the crawlable `?eventId=<id>` form (the reason this
 * branch exists), browser mode the plain path, hash mode the legacy `#!` form.
 * Tracking/filter params are deliberately left out so the canonical stays clean.
 *
 * `base` is the page the route hangs off (see `useCanonicalBase`). In query mode
 * the route params are merged into it exactly as the API sitemap merges them, so
 * the canonical and the sitemap `<loc>` are the same string.
 */
export const buildCanonicalHref = (
  path: string,
  routerType: string,
  base: string,
  home?: string,
): string => {
  if (routerType === RouterTypes.QUERY) {
    const at = path.indexOf('?');
    const routePathname = at === -1 ? path : path.slice(0, at);
    const search = at === -1 ? '' : path.slice(at);
    const encoded = encodeRoute({ pathname: routePathname, search }, '', {
      listIsHome: isExperienceListHome(home),
    });
    return mergeSearchIntoUrl(base, encoded);
  }

  const [page] = base.split('?');

  if (routerType === RouterTypes.BROWSER) {
    return `${page}${path}`;
  }

  return `${page}#!${path}`;
};

export const buildCanonicalPath = ({
  collectionId,
  eventId,
  groupId,
}: CanonicalLinkParams): string | undefined => {
  if (eventId) return `/event/${eventId}`;
  if (collectionId && groupId)
    return `/collection/${collectionId}/group/${groupId}`;
  if (collectionId) return `/collection/${collectionId}`;
  return undefined;
};

/** The absolute canonical URL for an internal route, or undefined while the route isn't known yet. */
export const useCanonicalHref = (path: string | undefined) => {
  const { routerType, home } = useEmbedConfig();
  const base = useCanonicalBase();
  return path ? buildCanonicalHref(path, routerType, base, home) : undefined;
};

/**
 * Manages the canonical link tag for the current page.
 * Removes any existing canonical link and adds a new one
 * directly to the document head via DOM manipulation.
 * Skips creation when path is undefined (e.g. during loading).
 * Cleans up on unmount.
 *
 * Listing pages render `ListingSeo` instead, which sets the canonical along with
 * the rest of the page's SEO tags; this hook remains for the sub-pages.
 */
export const useCanonicalLink = (path: string | undefined) => {
  const canonicalUrl = useCanonicalHref(path);

  useEffect(() => {
    if (!canonicalUrl) {
      return undefined;
    }

    const existingCanonical = document.querySelector("link[rel='canonical']");
    if (existingCanonical) {
      existingCanonical.remove();
    }

    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', canonicalUrl);
    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, [canonicalUrl]);
};
